import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Box, Button, Tab, Tabs, Tooltip } from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { AdministrativeActionsModal, FilterDrawer } from '../../components'
import {
  AdministrativeActionsModalProvider,
  useAdministrativeActionsModal,
} from '../../contexts/AdministrativeActionsModalContext'
import {
  InterventionProvider,
  useInterventionsPage
} from '../../contexts/InterventionContext'
import { InterventionsTab } from '../InterventionsTab'
import { useConfigContextInfo } from '../../contexts/ConfigContext'
import { ROLES } from '../../utils/roles'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f1f3f5;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 44px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const TabContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const a11yProps = index => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

const TabContent = ({ children, value, index, ...other }) => {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <TabContentContainer>{children}</TabContentContainer>}
    </div>
  )
}

const InterventionsPage = () => {
  const [page, setPage] = useState(0)
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const { openModal, setLink } = useAdministrativeActionsModal()
  const {
    loading: loadingData,
    rows,
    filterOptions,
    setSelectedFilterOptions,
    inputValue,
    handleType,
    dateRange,
    handleDateRange,
    selectedTab,
    handleChangeSelectedTab,
    selectedFilterOptions,
    onRead,
    fetchData,
    updateAnnotation,
  } = useInterventionsPage()
  const { role } = useConfigContextInfo()
  const { t } = useTranslation('common')

  const handleButtonClick = () => {
    setLink(setLink)
    openModal()
  }

  useEffect(() => {
    if (dateRange) fetchData({ dateRange })
  }, [dateRange])

  useEffect(() => {
    setPage(0)
  }, [selectedFilterOptions])

  return (
    <Container>
      <ContentContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>{t('annotations.header')}</span>
            <Tooltip
              title={t('annotations.tooltip')}
              placement='right'
              arrow
            >
              <HelpOutline fontSize='18' />
            </Tooltip>
          </div>
          {role !== ROLES.EMPLOYEE && (
            <Button
              variant='outlined'
              onClick={handleButtonClick}
            >
              {t('annotations.create')}
            </Button>
          )}
        </Header>
        <BodyContainer>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedTab}
              onChange={handleChangeSelectedTab}
              aria-label='notification-tabs'
            >
              <Tab
                label={t('annotations.received')}
                {...a11yProps(0)}
              />
              {role !== ROLES.EMPLOYEE && (
                <Tab
                  label={t('annotations.sent')}
                  {...a11yProps(1)}
                />
              )}
            </Tabs>
          </Box>
          <TabContent
            value={selectedTab}
            index={0}
          >
            <InterventionsTab
              page={page}
              onChangePage={setPage}
              loadingData={loadingData}
              rows={rows}
              inputValue={inputValue}
              handleType={handleType}
              dateRange={dateRange}
              handleDateRange={handleDateRange}
              selectedFilterOptions={selectedFilterOptions}
              setOpenFilterDrawer={setOpenFilterDrawer}
              updateAnnotation={updateAnnotation}
            />
          </TabContent>
          <TabContent
            value={selectedTab}
            index={1}
          >
            <InterventionsTab
              page={page}
              onChangePage={setPage}
              loadingData={loadingData}
              rows={rows}
              inputValue={inputValue}
              handleType={handleType}
              dateRange={dateRange}
              handleDateRange={handleDateRange}
              selectedFilterOptions={selectedFilterOptions}
              setOpenFilterDrawer={setOpenFilterDrawer}
              updateAnnotation={updateAnnotation}
            />
          </TabContent>
        </BodyContainer>
      </ContentContainer>
      <AdministrativeActionsModal
        onSend={() => {
          handleDateRange({ value: [new Date(), new Date()] })
        }}
        enableAnnotations
      />
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={setSelectedFilterOptions}
        loading={loadingData}
        options={filterOptions}
        withSeverity
        withShift
        withPosition
        withTeam
        withInterventionStatus
      />
    </Container>
  )
}

const Interventions = () => {
  return (
    <AdministrativeActionsModalProvider>
      <InterventionProvider>
        <InterventionsPage />
      </InterventionProvider>
    </AdministrativeActionsModalProvider>
  )
}

export default Interventions
